/**
 * The full text of one external record, fetched when it is opened.
 *
 * The list carries an excerpt -- Gmail's snippet, two lines of it -- because a
 * hundred full message bodies is a hundred times the payload for text nobody
 * has asked to read. This is the other half of that trade: the preview asks for
 * the body of the one record on screen.
 *
 * Built as an external store rather than component state so the cache is shared
 * between every surface that opens the same record, and so the effect never
 * calls setState synchronously. Same shape as `recordsView.ts`.
 */

import { useEffect, useSyncExternalStore } from "react";

import { useCollections } from "@/api/hooks";
import { HGAnyRecord, HGCollection } from "@/common/types";

import { getProvider } from "./registry";
import { getAccessToken } from "./tokens";

/**
 * Bodies already fetched, keyed by the synthetic record id. Bounded because a
 * long session that reads a lot of mail would otherwise hold all of it.
 */
const CACHE_LIMIT = 40;
const bodies = new Map<string, string>();
const inFlight = new Set<string>();
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function remember(key: string, body: string) {
  // Re-inserting moves the key to the end, so the first is the oldest.
  bodies.delete(key);
  bodies.set(key, body);
  while (bodies.size > CACHE_LIMIT) {
    const oldest = bodies.keys().next().value;
    if (oldest === undefined) break;
    bodies.delete(oldest);
  }
}

/** Dropped on sign-out along with everything else keyed to an account. */
export function forgetExternalBodies() {
  bodies.clear();
  inFlight.clear();
  notify();
}

export function useExternalRecordBody(
  record: HGAnyRecord | null,
): string | null {
  const collections = useCollections();
  const external = record?.external;
  const key = external ? (record?.id ?? null) : null;

  const body = useSyncExternalStore(subscribe, () =>
    key ? (bodies.get(key) ?? null) : null,
  );

  const collectionId = external?.collectionId;
  const providerKey = external?.providerKey;
  const externalId = external?.externalId;

  useEffect(() => {
    if (!key || !providerKey || !externalId || !collectionId) return;
    if (bodies.has(key) || inFlight.has(key)) return;

    const provider = getProvider(providerKey);
    if (!provider?.fetchDetail) return;

    const collection = collections.find(
      (c: HGCollection) => c.id === collectionId,
    );
    const connectionId = collection?.connection_id;
    if (!connectionId) return;

    const controller = new AbortController();
    inFlight.add(key);

    (async () => {
      try {
        const accessToken = await getAccessToken(connectionId);
        const text = await provider.fetchDetail!({
          accessToken,
          config: collection.provider_config ?? {},
          externalId,
          signal: controller.signal,
        });
        remember(key, text);
        notify();
      } catch {
        // The excerpt stays on screen. A failed body is worth neither an error
        // pane nor a retry loop: the row is still readable, and the permalink
        // out to the source system is right there.
      } finally {
        inFlight.delete(key);
      }
    })();

    return () => controller.abort();
  }, [key, providerKey, externalId, collectionId, collections]);

  return body;
}
